"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChefHat, ArrowRight } from "lucide-react";

export default function KioskRouteScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Routes to the kitchen", "Rutea a la cocina")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Lands on the KDS. ", "Cae en el KDS. ")}
            <span className="text-redy-charcoal/30">{t("Like any ticket.", "Como cualquier ticket.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "The kitchen doesn't know it came from the kiosk vs. from the counter. Same routing, same prep, same bump. Source tagged \"KIOSK · #3\" for end-of-shift reporting.",
              "La cocina no sabe si vino del kiosko o del mostrador. Mismo ruteo, mismo prep, mismo bump. Source tageado \"KIOSK · #3\" para el reporte al cerrar."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="max-w-[920px] mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-6 items-center">
            {/* Kiosk side */}
            <div className="bg-white border border-black/5 rounded-[20px] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                  {t("Kiosk #3", "Kiosko #3")}
                </div>
                <div
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: "#F4722B" }}
                >
                  {t("Submitted", "Enviado")}
                </div>
              </div>
              <div className="space-y-2" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                <div className="flex justify-between text-[12px]">
                  <span className="text-redy-charcoal font-bold">Birria × 2</span>
                  <span className="text-redy-charcoal tabular-nums">$9.00</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-redy-charcoal font-bold">Agua Fresca</span>
                  <span className="text-redy-charcoal tabular-nums">$4.00</span>
                </div>
                <div className="border-t border-dashed border-black/10 my-3" />
                <div className="flex justify-between items-baseline">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary">Total</span>
                  <span className="text-[20px] font-black text-redy-charcoal tabular-nums">$13.50</span>
                </div>
              </div>
              <div className="mt-4 text-[10px] font-medium text-redy-secondary text-center">
                {t("Paid via Apple Pay · 11:42:08", "Pagado vía Apple Pay · 11:42:08")}
              </div>
            </div>

            {/* Arrow with sync indicator */}
            <div className="flex flex-col items-center gap-2 py-4">
              <motion.div
                animate={{ x: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                  boxShadow: "0 10px 30px rgba(244,114,43,0.30)",
                }}
              >
                <ArrowRight className="w-5 h-5 text-white" strokeWidth={3} />
              </motion.div>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#D7263D" }}>
                {t("In seconds", "En segundos")}
              </div>
            </div>

            {/* KDS side */}
            <div className="bg-[#0a1f1c] rounded-[20px] p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-white/70" />
                  <div className="text-[10px] font-black tracking-widest uppercase text-white/55">
                    KDS · {t("Kitchen", "Cocina")}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                  <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                    Live
                  </span>
                </div>
              </div>

              {/* New ticket */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -5 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="rounded-[10px] p-3 border"
                style={{
                  backgroundColor: "rgba(244,114,43,0.12)",
                  borderColor: "rgba(244,114,43,0.4)",
                }}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="text-[12px] font-black text-white">#107</div>
                  <div
                    className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
                    style={{ backgroundColor: "#F4722B" }}
                  >
                    KIOSK 3
                  </div>
                </div>
                <div className="space-y-0.5 text-[11px] text-white/85">
                  <div>2× Birria</div>
                  <div>1× Agua Fresca</div>
                </div>
                <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between">
                  <div className="text-[9px] text-white/55">11:42:08</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                    {t("New", "Nuevo")}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2.5 rounded-full shadow-sm">
              <ChefHat className="w-3.5 h-3.5" style={{ color: "#D7263D" }} />
              <span className="text-[12px] font-bold text-redy-charcoal">
                {t("Same flow as counter orders. Same KDS. Same prep.", "Mismo flow que orden en mostrador. Mismo KDS. Mismo prep.")}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
